/**
 * Timeline Export Utilities
 *
 * Functions for exporting project timeline to various formats:
 * - YAML (AI-ready)
 * - Markdown (AI-ready)
 * - HTML (Presentable/Print)
 * - PDF (Document)
 *
 * @module utils/timelineExport
 */

import {
  downloadYAML,
  downloadMarkdown,
  downloadHTML,
} from './exportHelpers';
import {
  EVENT_TYPE_LABELS,
} from '../services/timelineService';
import { formatCurrency as formatCurrencyShared } from './formatters';

/**
 * Format date for export
 * @param {string} timestamp - ISO timestamp
 * @returns {string} Formatted date
 */
const formatDate = (timestamp) => {
  const date = new Date(timestamp);
  return date.toLocaleDateString('es-CR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

/**
 * Format time for export
 * @param {string} timestamp - ISO timestamp
 * @returns {string} Formatted time
 */
const formatTime = (timestamp) => {
  const date = new Date(timestamp);
  return date.toLocaleTimeString('es-CR', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Format currency for export.
 *
 * Esto hardcodeaba `currency: 'CRC'`. Los eventos de `project_timeline` NO
 * tienen columna de moneda — ninguna tabla salvo `invoices`, `purchases` e
 * `inventory_batches` la tiene — asi que ese `₡` no salia de un dato, salia de
 * un supuesto. Contra la regla de #617, se va: el monto se exporta sin rotulo.
 *
 * Bonus medido: en la rama PDF (linea ~484) el `₡` ademas estaba ROMPIENDO el
 * renglon. jsPDF usa las Type1 estandar con `/Encoding /WinAnsiEncoding` y sin
 * `ToUnicode`; U+20A1 no esta en WinAnsi, asi que jsPDF pasaba la cadena entera
 * a dos bytes por caracter y el visor la leia byte a byte. Sacar el simbolo deja
 * la cadena en ASCII y el renglon legible.
 *
 * @param {number} amount - Amount
 * @returns {string} Formatted amount (sin simbolo de moneda)
 */
const formatCurrency = (amount) => {
  if (!amount) return '';
  return formatCurrencyShared(amount);
};

/**
 * Group events by date
 * @param {Array} events - Timeline events
 * @returns {Object} Events grouped by date
 */
const groupEventsByDate = (events) => {
  const grouped = {};
  events.forEach(event => {
    const dateKey = new Date(event.timestamp).toISOString().split('T')[0];
    if (!grouped[dateKey]) {
      grouped[dateKey] = [];
    }
    grouped[dateKey].push(event);
  });
  return grouped;
};

// ============================================
// YAML Export (AI-Ready)
// ============================================

/**
 * Export timeline to YAML format
 * @param {Object} data - Export data
 * @param {string} filename - Filename without extension
 */
export const exportToYAML = (data, filename) => {
  const { project, events, exportDate } = data;

  const yamlContent = `# Bitacora del Proyecto
# Exportado: ${exportDate}
# Formato: YAML (AI-Ready)

project:
  id: "${project.id || ''}"
  number: "${project.number || ''}"
  title: "${project.title || ''}"
  status: "${project.status || ''}"
  client: "${project.client || ''}"

export_metadata:
  date: "${exportDate}"
  total_events: ${events.length}
  format: "yaml"
  version: "1.0"

timeline:
${events.map(event => `  - timestamp: "${event.timestamp}"
    event_type: "${event.event_type}"
    source: "${event.source}"
    description: "${(event.description || '').replace(/"/g, '\\"')}"
    user: "${event.user_name || 'Sistema'}"
    is_milestone: ${event.is_milestone || false}${event.amount ? `
    amount: ${event.amount}` : ''}${event.photo_url ? `
    photo_url: "${event.photo_url}"` : ''}${event.metadata ? `
    metadata: ${JSON.stringify(event.metadata)}` : ''}`).join('\n')}
`;

  downloadYAML(yamlContent, filename);
};

// ============================================
// Markdown Export (AI-Ready)
// ============================================

/**
 * Export timeline to Markdown format
 * @param {Object} data - Export data
 * @param {string} filename - Filename without extension
 */
export const exportToMarkdown = (data, filename) => {
  const { project, events, exportDate } = data;
  const groupedEvents = groupEventsByDate(events);
  const sortedDates = Object.keys(groupedEvents).sort((a, b) => new Date(b) - new Date(a));

  let mdContent = `# Bitacora: ${project.number || 'Proyecto'}\n\n`;
  mdContent += `**Proyecto:** ${project.title || 'Sin titulo'}\n`;
  mdContent += `**Cliente:** ${project.client || 'N/A'}\n`;
  mdContent += `**Estado:** ${project.status || 'N/A'}\n`;
  mdContent += `**Exportado:** ${new Date(exportDate).toLocaleString('es-CR')}\n`;
  mdContent += `**Total de eventos:** ${events.length}\n\n`;
  mdContent += `---\n\n`;

  sortedDates.forEach(dateKey => {
    const dateEvents = groupedEvents[dateKey];
    mdContent += `## ${formatDate(dateKey)}\n\n`;

    dateEvents.forEach(event => {
      const milestone = event.is_milestone ? ' **[HITO]**' : '';
      const eventLabel = EVENT_TYPE_LABELS[event.event_type] || event.event_type;
      const amount = event.amount ? ` - ${formatCurrency(event.amount)}` : '';

      mdContent += `- **${formatTime(event.timestamp)}**${milestone} - ${eventLabel}\n`;
      mdContent += `  - ${event.description || 'Sin descripcion'}${amount}\n`;
      mdContent += `  - *Por: ${event.user_name || 'Sistema'}*\n`;

      if (event.photo_url) {
        mdContent += `  - ![Foto](${event.photo_url})\n`;
      }

      mdContent += `\n`;
    });
  });

  mdContent += `---\n\n`;
  mdContent += `*Generado automaticamente por ProMedic - Sistema de Gestion de Proyectos*\n`;

  downloadMarkdown(mdContent, filename);
};

// ============================================
// HTML Export (Presentable/Print)
// ============================================

/**
 * Export timeline to HTML format
 * @param {Object} data - Export data
 * @param {string} filename - Filename without extension
 */
export const exportToHTML = (data, filename) => {
  const { project, events, exportDate } = data;
  const groupedEvents = groupEventsByDate(events);
  const sortedDates = Object.keys(groupedEvents).sort((a, b) => new Date(b) - new Date(a));

  const htmlContent = `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Bitacora - ${project.number || 'Proyecto'}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
      line-height: 1.6;
      color: #333;
      max-width: 800px;
      margin: 0 auto;
      padding: 2rem;
      background: #f8f9fa;
    }
    .header {
      background: white;
      padding: 2rem;
      border-radius: 8px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      margin-bottom: 2rem;
    }
    .header h1 {
      color: #0d6efd;
      margin-bottom: 1rem;
    }
    .header-info {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 1rem;
    }
    .header-info div {
      padding: 0.5rem;
      background: #f8f9fa;
      border-radius: 4px;
    }
    .header-info label {
      font-weight: 600;
      color: #6c757d;
      font-size: 0.85rem;
    }
    .date-group {
      margin-bottom: 2rem;
    }
    .date-header {
      background: #0d6efd;
      color: white;
      padding: 0.75rem 1rem;
      border-radius: 8px 8px 0 0;
      font-weight: 600;
    }
    .events-container {
      background: white;
      border-radius: 0 0 8px 8px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }
    .event {
      padding: 1rem;
      border-bottom: 1px solid #e9ecef;
    }
    .event:last-child {
      border-bottom: none;
    }
    .event-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 0.5rem;
    }
    .event-time {
      font-weight: 600;
      color: #495057;
    }
    .event-type {
      background: #e9ecef;
      padding: 0.25rem 0.5rem;
      border-radius: 4px;
      font-size: 0.8rem;
    }
    .milestone .event-type {
      background: #ffc107;
      color: #000;
    }
    .event-description {
      margin-bottom: 0.5rem;
    }
    .event-user {
      font-size: 0.85rem;
      color: #6c757d;
    }
    .event-amount {
      display: inline-block;
      background: #dc3545;
      color: white;
      padding: 0.25rem 0.5rem;
      border-radius: 4px;
      font-size: 0.85rem;
      margin-top: 0.5rem;
    }
    .event-photo {
      max-width: 200px;
      margin-top: 0.5rem;
      border-radius: 4px;
    }
    .footer {
      text-align: center;
      padding: 2rem;
      color: #6c757d;
      font-size: 0.85rem;
    }
    @media print {
      body {
        background: white;
        padding: 1rem;
      }
      .header, .events-container {
        box-shadow: none;
        border: 1px solid #dee2e6;
      }
      .event {
        page-break-inside: avoid;
      }
    }
  </style>
</head>
<body>
  <div class="header">
    <h1>Bitacora del Proyecto</h1>
    <h2>${project.number || ''} - ${project.title || 'Sin titulo'}</h2>
    <div class="header-info">
      <div><label>Cliente:</label><br>${project.client || 'N/A'}</div>
      <div><label>Estado:</label><br>${project.status || 'N/A'}</div>
      <div><label>Total Eventos:</label><br>${events.length}</div>
      <div><label>Exportado:</label><br>${new Date(exportDate).toLocaleString('es-CR')}</div>
    </div>
  </div>

  ${sortedDates.map(dateKey => {
    const dateEvents = groupedEvents[dateKey];
    return `
  <div class="date-group">
    <div class="date-header">${formatDate(dateKey)}</div>
    <div class="events-container">
      ${dateEvents.map(event => `
      <div class="event ${event.is_milestone ? 'milestone' : ''}">
        <div class="event-header">
          <span class="event-time">${formatTime(event.timestamp)}</span>
          <span class="event-type">${event.is_milestone ? '⚑ HITO - ' : ''}${EVENT_TYPE_LABELS[event.event_type] || event.event_type}</span>
        </div>
        <div class="event-description">${event.description || 'Sin descripcion'}</div>
        ${event.amount ? `<span class="event-amount">${formatCurrency(event.amount)}</span>` : ''}
        ${event.photo_url ? `<img src="${event.photo_url}" alt="Foto" class="event-photo">` : ''}
        <div class="event-user">Por: ${event.user_name || 'Sistema'}</div>
      </div>
      `).join('')}
    </div>
  </div>`;
  }).join('')}

  <div class="footer">
    <p>Documento generado automaticamente por ProMedic - Sistema de Gestion de Proyectos</p>
    <p>&copy; ${new Date().getFullYear()} ProMedic Group</p>
  </div>
</body>
</html>`;

  downloadHTML(htmlContent, filename);
};

// ============================================
// PDF Export (Document)
// ============================================

/**
 * Export timeline to PDF format
 * Uses jspdf library for PDF generation
 * @param {Object} data - Export data
 * @param {string} filename - Filename without extension
 */
export const exportToPDF = async (data, filename) => {
  const { project, events, exportDate } = data;

  // Dynamically import jspdf to reduce bundle size
  const { jsPDF } = await import('jspdf');

  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  const contentWidth = pageWidth - 2 * margin;
  let yPos = margin;

  // Helper to add new page if needed
  const checkPageBreak = (requiredSpace = 30) => {
    if (yPos + requiredSpace > pageHeight - margin) {
      doc.addPage();
      yPos = margin;
      return true;
    }
    return false;
  };

  // Header
  doc.setFillColor(13, 110, 253);
  doc.rect(0, 0, pageWidth, 40, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('Bitacora del Proyecto', margin, 15);

  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text(`${project.number || ''} - ${project.title || 'Sin titulo'}`, margin, 25);
  doc.text(`Cliente: ${project.client || 'N/A'}`, margin, 32);

  yPos = 50;

  // Project info box
  doc.setTextColor(0, 0, 0);
  doc.setFillColor(248, 249, 250);
  doc.rect(margin, yPos, contentWidth, 25, 'F');

  doc.setFontSize(10);
  doc.text(`Estado: ${project.status || 'N/A'}`, margin + 5, yPos + 8);
  doc.text(`Total Eventos: ${events.length}`, margin + 5, yPos + 16);
  doc.text(`Exportado: ${new Date(exportDate).toLocaleString('es-CR')}`, pageWidth / 2, yPos + 8);

  yPos += 35;

  // Group events by date
  const groupedEvents = groupEventsByDate(events);
  const sortedDates = Object.keys(groupedEvents).sort((a, b) => new Date(b) - new Date(a));

  // Events
  sortedDates.forEach(dateKey => {
    const dateEvents = groupedEvents[dateKey];

    checkPageBreak(20);

    // Date header
    doc.setFillColor(13, 110, 253);
    doc.rect(margin, yPos, contentWidth, 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(formatDate(dateKey), margin + 3, yPos + 5.5);

    yPos += 12;
    doc.setTextColor(0, 0, 0);

    dateEvents.forEach(event => {
      checkPageBreak(25);

      // Event card
      const cardHeight = 18 + (event.amount ? 5 : 0);

      if (event.is_milestone) {
        doc.setDrawColor(255, 193, 7);
        doc.setLineWidth(1);
        doc.rect(margin, yPos, contentWidth, cardHeight, 'S');
      } else {
        doc.setDrawColor(233, 236, 239);
        doc.setLineWidth(0.5);
        doc.rect(margin, yPos, contentWidth, cardHeight, 'S');
      }

      // Time and type
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text(formatTime(event.timestamp), margin + 3, yPos + 5);

      const typeLabel = (event.is_milestone ? 'HITO - ' : '') +
        (EVENT_TYPE_LABELS[event.event_type] || event.event_type);
      doc.setFont('helvetica', 'normal');
      doc.text(typeLabel, pageWidth - margin - 3 - doc.getTextWidth(typeLabel), yPos + 5);

      // Description
      doc.setFontSize(10);
      const description = event.description || 'Sin descripcion';
      const splitDescription = doc.splitTextToSize(description, contentWidth - 10);
      doc.text(splitDescription[0], margin + 3, yPos + 11);

      // User
      doc.setFontSize(8);
      doc.setTextColor(108, 117, 125);
      doc.text(`Por: ${event.user_name || 'Sistema'}`, margin + 3, yPos + 16);

      // Amount
      if (event.amount) {
        doc.setTextColor(220, 53, 69);
        doc.text(formatCurrency(event.amount), pageWidth - margin - 3 - doc.getTextWidth(formatCurrency(event.amount)), yPos + 16);
      }

      doc.setTextColor(0, 0, 0);
      yPos += cardHeight + 3;
    });

    yPos += 5;
  });

  // Footer
  checkPageBreak(20);
  doc.setFontSize(8);
  doc.setTextColor(108, 117, 125);
  doc.text(
    `Documento generado por ProMedic - Sistema de Gestion de Proyectos - ${new Date().getFullYear()}`,
    pageWidth / 2,
    pageHeight - 10,
    { align: 'center' }
  );

  // Save
  doc.save(`${filename}.pdf`);
};

const timelineExport = {
  exportToYAML,
  exportToMarkdown,
  exportToHTML,
  exportToPDF,
};

export default timelineExport;
